import { supabase } from "@supabaseClient/supabaseClient";

/**
 * Customer status type
 */
export type CustomerStatus = "active" | "inactive" | "archived";

/**
 * Customer type (individual, business, etc.)
 */
export type CustomerType = "individual" | "business" | "wholesale" | null;

/**
 * Address type for customer addresses
 */
export type AddressType = "shipping" | "billing" | "delivery" | "other";

/**
 * Customer address (without id - as stored in metadata)
 */
export interface ICustomerAddress {
  type: AddressType;
  street: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  is_default: boolean;
}

/**
 * Customer address with id (for form state management)
 */
export interface ICustomerAddressWithId extends ICustomerAddress {
  id: string;
}

/**
 * Customer interface matching the database schema
 */
export interface ICustomer {
  id: string;
  organization_id: string;
  external_id?: string | null;
  name: string;
  email?: string | null;
  phone?: string | null;
  status: CustomerStatus;
  customer_type?: CustomerType;
  metadata?: Record<string, string | number | boolean | null | undefined>;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

/**
 * Customer with related orders count (for list views)
 */
export interface ICustomerWithOrderCount extends ICustomer {
  order_count?: number;
  total_spent?: number;
  first_order_date?: string;
  last_order_date?: string;
}

/**
 * Type for Supabase response when querying customers with nested orders
 */
interface ICustomerWithOrdersResponse extends ICustomer {
  orders: Array<{
    id: string;
    created_at: string;
    status: string;
  }>;
}

export class CustomersClient {
  /**
   * Get all customers for an organization
   */
  static async getCustomersByOrganizationId(
    organization_id: string
  ): Promise<ICustomer[]> {
    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq("organization_id", organization_id)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      throw error;
    }
    return data || [];
  }

  /**
   * Get a customer by ID
   */
  static async getCustomerById(customer_id: string): Promise<ICustomer> {
    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq("id", customer_id)
      .is("deleted_at", null)
      .single();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Find a customer by email within an organization
   * Email is normalized to lowercase for matching
   */
  static async getCustomerByEmail(
    email: string,
    organization_id: string
  ): Promise<ICustomer | null> {
    // Normalize email to lowercase for consistent matching
    const normalizedEmail = email.toLowerCase().trim();

    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq("organization_id", organization_id)
      .ilike("email", normalizedEmail)
      .is("deleted_at", null)
      .maybeSingle();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Find a customer by external_id within an organization
   */
  static async getCustomerByExternalId(
    external_id: string,
    organization_id: string
  ): Promise<ICustomer | null> {
    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq("organization_id", organization_id)
      .eq("external_id", external_id)
      .is("deleted_at", null)
      .maybeSingle();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Create a new customer
   * Email is automatically normalized to lowercase
   */
  static async createCustomer(
    customer: Omit<ICustomer, "id" | "created_at" | "updated_at" | "deleted_at">
  ): Promise<ICustomer> {
    // Normalize email to lowercase if provided
    const normalizedCustomer = {
      ...customer,
      email: customer.email ? customer.email.toLowerCase().trim() : null,
    };

    const { data, error } = await supabase
      .from("customers")
      .insert([normalizedCustomer])
      .select()
      .single();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Update a customer
   * Email is automatically normalized to lowercase if provided
   */
  static async updateCustomer(
    customer: Omit<ICustomer, "created_at" | "updated_at" | "deleted_at">
  ): Promise<ICustomer> {
    // Normalize email to lowercase if provided
    const normalizedCustomer = {
      ...customer,
      email: customer.email ? customer.email.toLowerCase().trim() : customer.email,
    };

    const { data, error } = await supabase
      .from("customers")
      .update(normalizedCustomer)
      .eq("id", customer.id)
      .select()
      .single();

    if (error) {
      throw error;
    }
    return data;
  }

  /**
   * Soft delete a customer (sets deleted_at timestamp)
   */
  static async deleteCustomer(customer_id: string): Promise<void> {
    const { error } = await supabase
      .from("customers")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", customer_id);

    if (error) {
      throw error;
    }
  }

  /**
   * Find or create a customer by email
   * Useful when creating orders - automatically links to existing customer
   */
  static async findOrCreateCustomer(customerData: {
    organization_id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
    external_id?: string | null;
    customer_type?: CustomerType;
    metadata?: Record<string, string | number | boolean | null | undefined>;
  }): Promise<ICustomer> {
    // First, try to find by email if provided
    if (customerData.email) {
      const existing = await this.getCustomerByEmail(
        customerData.email,
        customerData.organization_id
      );
      if (existing) {
        // Update existing customer if name or other fields changed
        if (
          existing.name !== customerData.name ||
          existing.phone !== customerData.phone ||
          existing.customer_type !== customerData.customer_type
        ) {
          return await this.updateCustomer({
            ...existing,
            name: customerData.name,
            phone: customerData.phone ?? existing.phone,
            customer_type: customerData.customer_type ?? existing.customer_type,
          });
        }
        return existing;
      }
    }

    // Try to find by external_id if provided
    if (customerData.external_id) {
      const existing = await this.getCustomerByExternalId(
        customerData.external_id,
        customerData.organization_id
      );
      if (existing) {
        // Update existing customer
        return await this.updateCustomer({
          ...existing,
          name: customerData.name,
          email: customerData.email ?? existing.email,
          phone: customerData.phone ?? existing.phone,
          customer_type: customerData.customer_type ?? existing.customer_type,
        });
      }
    }

    // Create new customer
    // Normalize email to lowercase if provided
    const normalizedCustomerData = {
      ...customerData,
      email: customerData.email ? customerData.email.toLowerCase().trim() : null,
      status: "active" as CustomerStatus,
    };

    return await this.createCustomer(normalizedCustomerData);
  }

  /**
   * Search customers by name or email
   */
  static async searchCustomers(
    organization_id: string,
    searchTerm: string
  ): Promise<ICustomer[]> {
    const normalizedSearch = searchTerm.toLowerCase().trim();

    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq("organization_id", organization_id)
      .is("deleted_at", null)
      .or(`name.ilike.%${normalizedSearch}%,email.ilike.%${normalizedSearch}%`)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      throw error;
    }
    return data || [];
  }

  /**
   * Get customers with order statistics
   * Useful for customer list views with analytics
   */
  static async getCustomersWithOrderStats(
    organization_id: string
  ): Promise<ICustomerWithOrderCount[]> {
    const { data, error } = await supabase
      .from("customers")
      .select(
        `
        *,
        orders:orders!customer_id (
          id,
          created_at,
          status
        )
      `
      )
      .eq("organization_id", organization_id)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      throw error;
    }

    // Transform the data to include order statistics
    return (data || []).map(
      (customer: ICustomerWithOrdersResponse): ICustomerWithOrderCount => {
        const orders = customer.orders || [];

        const orderDates = orders
          .map((o) => new Date(o.created_at))
          .sort((a, b) => a.getTime() - b.getTime());

        // Destructure to exclude the orders field
        const { ...customerWithoutOrders } = customer;

        return {
          ...customerWithoutOrders,
          order_count: orders.length,
          first_order_date:
            orderDates.length > 0 ? orderDates[0].toISOString() : undefined,
          last_order_date:
            orderDates.length > 0
              ? orderDates[orderDates.length - 1].toISOString()
              : undefined,
        };
      }
    );
  }
}
